'use client';

import React from 'react';
import { ResourceDownloadIcon, ExternalLinkIcon } from '../Icons/Icons';

// Type definitions for download CTA data structure
interface FileObject {
  url?: string;
  title?: string;
  filename?: string;
  [key: string]: any; // Allow other Contentstack fields
}

interface DownloadCTAItem {
  type?:
    | 'Download Related Files'
    | 'Code Samples'
    | 'Exercise Files'
    | 'Custom CTA'
    | 'View FAQ'
    | string
    | null;
  file?: FileObject;
  url?: string;
  custom_icon?: FileObject;
  custom_type?: string;
}

interface MaterialsTabProps {
  ctaData?: DownloadCTAItem[];
  faqs?: unknown[]; // Optional, for filtering if needed
}

const MaterialsTab: React.FC<MaterialsTabProps> = ({ ctaData, faqs }) => {
  // Filter out "View FAQ" items
  const filteredData = ctaData?.filter((item) => item.type !== 'View FAQ') || [];

  if (filteredData.length === 0) {
    return null;
  }

  // Helper function to get the appropriate icon for each CTA type
  const getIcon = (item: DownloadCTAItem) => {
    // Use custom icon if available
    if (item.custom_icon?.url) {
      return <img src={item.custom_icon.url} alt="" className="h-6 w-6" aria-hidden="true" />;
    }

    // Use ExternalLinkIcon if item.url is used (external link)
    if (item.url && item.url.trim() && !item.file?.url) {
      return <ExternalLinkIcon className="text-[var(--Icons-Medium)]" />;
    }

    // Default to ResourceDownloadIcon for file downloads
    return <ResourceDownloadIcon className="text-[var(--Icons-Medium)]" />;
  };

  // Helper function to get the label text
  const getLabel = (item: DownloadCTAItem) => {
    // For Custom CTA or items with custom_type, use custom_type
    if ((item.type === 'Custom CTA' || !item.type) && item.custom_type) {
      return item.custom_type;
    }
    return item.type || 'Resource';
  };

  // Helper function to get the link URL
  const getUrl = (item: DownloadCTAItem) => {
    // For Custom CTA or items with custom_type (null type), prioritize url over file
    if (item.type === 'Custom CTA' || (!item.type && item.custom_type)) {
      return (item.url && item.url.trim()) || item.file?.url || '#';
    }
    return item.file?.url || '#';
  };

  // Helper function to check if item should be rendered
  const shouldRenderItem = (item: DownloadCTAItem) => {
    // For standard types, render if file exists
    if (
      item.type === 'Download Related Files' ||
      item.type === 'Code Samples' ||
      item.type === 'Exercise Files'
    ) {
      return !!item.file?.url;
    }

    // For Custom CTA, render if either url or file exists
    if (item.type === 'Custom CTA') {
      return !!(item.url && item.url.trim()) || !!item.file?.url;
    }

    // Handle items with null/undefined type but has custom_type (treat as Custom CTA)
    if (!item.type && item.custom_type) {
      return !!(item.url && item.url.trim()) || !!item.file?.url;
    }

    return false;
  };

  // Filter items that should be rendered
  const renderableItems = filteredData.filter(shouldRenderItem);

  if (renderableItems.length === 0) {
    return null;
  }

  return (
    <>
      {renderableItems.map((item, index) => {
        const url = getUrl(item);
        const label = getLabel(item);
        const ariaLabel = `Download ${label}`;

        // Get file title or custom description
        const fileTitle = (item.file as any)?.title || (item.file as any)?.filename || label;

        return (
          <div key={index} className="bg-gradient-sheen mb-2 flex flex-row justify-between md:items-center gap-3 p-4">
            {/* Mobile: Label + Icon in row, Desktop: Label only */}
            <div className="flex w-[30%] flex-row items-center justify-between">
              <span className="cs-caption whitespace-nowrap ">{label}</span>
              <div className="hidden md:block">
                <span className="divider h-full" />
              </div>
            </div>

            {/* File name - full width on mobile, 50% on desktop */}
            <p className="cs-caption flex-1 truncate hidden md:block">{fileTitle}</p>

            {/* Icon visible on desktop, hidden on mobile */}
            <button
              className="cursor-pointer justify-end md:ml-4 md:flex"
              onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
              aria-label={ariaLabel}
            >
              {getIcon(item)}
            </button>
          </div>
        );
      })}
    </>
  );
};

export default MaterialsTab;
